import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Card from '../../components/common/Card/Card.tsx'
import StatusBar from '../../components/stripe/StatusBar/StatusBar.tsx'
import { useAuth } from '../../context/auth'
import useSubAccountDetails from '../../hooks/useSubAccountDetails.ts'
import { getLocalAgreement } from '../../utils/agreement/getLocalAgreement.ts'
import { isUserExistsInGroup } from '../../utils/userRights.ts'
import BusinessDetails from './BusinessDetails.tsx'
import Subscriptions from './Subscriptions.tsx'
import UsagePolicy from './UsagePolicy.tsx'

const Account = ({ page }: { page: string }) => {
  const navigate = useNavigate()
  const { subAccount, refetch } = useSubAccountDetails()
  const { state, dispatch } = useAuth()
  const isRestrictedUser = isUserExistsInGroup(
    state.groups,
    'dd_panel_restricted_users'
  )
  const isCertificateRequiredUser = isUserExistsInGroup(
    state.groups,
    'certificate_required_users_group_name'
  )
  const isPanelUser = isUserExistsInGroup(state.groups, 'dd_panel_users')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialGroups, _] = useState<string[] | null>(state.groups)
  const [step, setStep] = useState(1)
  const [localStorageStep, setLocalStorageStep] = useState(1)

  const steps = [
    { id: 1, label: 'Uzupełnij dane firmowe' },
    { id: 2, label: 'Zaakceptuj regulamin' },
    { id: 3, label: 'Zapłać opłatę licencyjną' },
    { id: 4, label: 'Subskrypcja aktywna' }
  ]

  const agreementStep = getLocalAgreement(state.email)

  useEffect(() => {
    if (!agreementStep) {
      localStorage.setItem(
        'agreement-' + state.email,
        JSON.stringify({ user: state.email, step: '1' })
      )
      setLocalStorageStep(Number(1))
    } else {
      setLocalStorageStep(Number(agreementStep))
    }
  }, [step])

  useEffect(() => {
    if (subAccount?.user_groups && step === 4) {
      dispatch({
        type: 'UPDATE_USER_GROUPS',
        payload: { groups: subAccount.user_groups }
      })
      localStorage.setItem('groups', JSON.stringify(subAccount.user_groups))
    }
  }, [subAccount, step])

  useEffect(() => {
    switch (localStorageStep.toString()) {
      case '1':
        setStep(1)
        navigate('/account/business-details')
        break

      case '2':
        setStep(localStorageStep)
        if (isRestrictedUser || (isPanelUser && subAccount?.agreement_exists)) {
          navigate('/account/usage-policy')
        }
        break

      case '3':
        setStep(3)
        if (isRestrictedUser || (isPanelUser && subAccount?.agreement_exists)) {
          navigate('/account/subscription')
        }
        break

      case '4':
        setStep(4)
        if (isRestrictedUser || (isPanelUser && subAccount?.agreement_exists)) {
          navigate('/account/subscription')
        }
        break

      default:
        setStep(1)
    }
  }, [subAccount, localStorageStep])

  useEffect(() => {
    const getFullAccessWithAgreement =
      isPanelUser && subAccount && subAccount.agreement_exists
    if (getFullAccessWithAgreement) {
      localStorage.setItem(
        'agreement-' + state.email,
        JSON.stringify({ user: state.email, step: '4' })
      )
      setStep(4)
    }
  }, [subAccount])

  useEffect(() => {
    if (JSON.stringify(initialGroups) !== JSON.stringify(state.groups)) {
      navigate('/application/care-giver')
    }
  }, [state.groups])

  return (
    <>
      <Breadcrumbs
        pageName={
          isRestrictedUser
            ? 'Rejestracja konta użytkownika panelu'
            : isCertificateRequiredUser
              ? 'Konto nie potwierdzone'
              : 'Konto aktywne'
        }
        breadcrumbs={[]}
      />

      <div className="mt-11">
        <Card>
          {(isRestrictedUser ||
            (isPanelUser && subAccount?.agreement_exists)) && (
            <div className={'flex flex-1 flex-col p-10'}>
              <StatusBar
                currentStep={step}
                steps={steps}
                localStorageKey={`agreement-${state.email}`}
              />
            </div>
          )}
        </Card>
        {page === 'business-details' && <BusinessDetails setStep={setStep} />}
        {(isRestrictedUser ||
          (isPanelUser && subAccount?.agreement_exists)) && (
          <>
            {page === 'usage-policy' && <UsagePolicy setStep={setStep} />}
            {page === 'subscription' && (
              <Subscriptions
                setStep={setStep}
                subAccount={subAccount}
                isRestrictedUser={isRestrictedUser}
                isPanelUser={isPanelUser}
                refetch={refetch}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}

export default Account
